import {
  Auth,
  NotificationChannel,
  UserNotificationPreferences,
  useNotificationPreferences,
} from "common";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Heading, Spinner, Text, XStack, YStack } from "tamagui";
import { Toggle } from "tamagui-config";

import { useShowToast } from "../../hooks/useShowToast";

type Channel = NotificationChannel.Sms | NotificationChannel.Email;

// Admins aren't linked to base stations: a channel switched on here means
// every alert from every base station (demo homes excepted), plus the
// follow-up when it clears. Category/resolve fields keep their all-on defaults.
export const Notifications = () => {
  const { t } = useTranslation();
  const { showErrorToast } = useShowToast();
  const { data, error, saving, save } = useNotificationPreferences();
  // Optimistic copy, seeded once so a refetch can't clobber a toggle in flight.
  const [prefs, setPrefs] = useState<UserNotificationPreferences | null>(null);
  const [contact, setContact] = useState<Record<string, string | undefined>>(
    {},
  );

  // On a failed load the hook hands back all-off defaults; seeding from those
  // would let one toggle overwrite the stored row. Never seed on an error.
  useEffect(() => {
    if (!error) setPrefs((current) => current ?? data);
  }, [data, error]);

  useEffect(() => {
    Auth.fetchUserAttributes()
      .then(setContact)
      .catch(() => undefined);
  }, []);

  const onToggle = async (channel: Channel, enabled: boolean) => {
    if (!prefs) return;
    const next = { ...prefs, [channel]: { ...prefs[channel], enabled } };
    setPrefs(next);
    if (!(await save(next))) {
      setPrefs((current) =>
        current
          ? { ...current, [channel]: { ...current[channel], enabled: !enabled } }
          : current,
      );
      showErrorToast(t("notifications.saveError"));
    }
  };

  const rows: { channel: Channel; title: string; lines: string[] }[] = [
    {
      channel: NotificationChannel.Sms,
      title: t("notifications.sms.title"),
      lines: [
        contact.phone_number
          ? t("notifications.sms.sendsTo", { phone: contact.phone_number })
          : t("notifications.sms.noPhone"),
        // Carrier toll-free verification: consent wording at the opt-in point.
        t("notifications.sms.disclosure"),
      ],
    },
    {
      channel: NotificationChannel.Email,
      title: t("notifications.email.title"),
      lines: [t("notifications.email.sendsTo", { email: contact.email ?? "" })],
    },
  ];

  return (
    <YStack gap="$4">
      <Heading size="$14" fontSize="$14" $xs={{ fontSize: "$12" }}>
        {t("sidebar.notifications")}
      </Heading>
      <Text color="$white10" maxWidth={760}>
        {t("notifications.description")}
      </Text>

      {!prefs && error ? (
        <Text color="$white10">{t("notifications.loadError")}</Text>
      ) : !prefs ? (
        <Spinner />
      ) : (
        rows.map((row) => (
          <XStack
            key={row.channel}
            alignItems="center"
            justifyContent="space-between"
            gap="$3"
            backgroundColor="$transparentBorder"
            padding="$4"
            borderRadius="$6"
            maxWidth={760}
          >
            <YStack gap="$1.5" flex={1}>
              <Text fontSize="$6" fontWeight="bold">
                {row.title}
              </Text>
              {row.lines.map((line) => (
                <Text key={line} color="$white10">
                  {line}
                </Text>
              ))}
            </YStack>
            <Toggle
              checked={prefs[row.channel].enabled}
              // Each save writes the whole row: one at a time, or a failed
              // save's revert can disagree with what the server kept.
              disabled={saving}
              onCheckedChange={(value) => onToggle(row.channel, value)}
            />
          </XStack>
        ))
      )}
    </YStack>
  );
};

export default Notifications;
